/* SamplePlaybookPage — public, no-auth marketing page.
 *
 * This page exists to convert prospects. It renders a real, anonymized appeal
 * playbook the same way an authenticated biller would see it inside the app.
 * No login required. Visitor picks one of 4 sample claims and reads the full
 * fighting playbook end-to-end.
 *
 * Routing: /sample-playbook (registered in App.js, OUTSIDE the Protected block).
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SectionHeading = ({ children }) => (
  <h3 className="text-[17px] font-bold text-slate-900 mt-7 mb-3 pb-1.5 border-b border-slate-200">
    {children}
  </h3>
);

const PlaybookRender = ({ pb, payor, amount, aging }) => (
  <div className="bg-white rounded-lg border border-slate-200 px-7 py-7 shadow-sm">
    {pb.urgency === "URGENT" && (
      <div className="rounded-md px-4 py-3 mb-5 bg-red-50 border-l-4 border-red-500 text-red-900 text-[14px]">
        <span className="font-bold">URGENT.</span> {pb.urgency_note}
      </div>
    )}
    {pb.urgency === "HIGH" && (
      <div className="rounded-md px-4 py-3 mb-5 bg-amber-50 border-l-4 border-amber-500 text-amber-900 text-[14px]">
        <span className="font-bold">High priority.</span> {pb.urgency_note}
      </div>
    )}
    {pb.escalation_required && (
      <div className="rounded-md px-4 py-3 mb-5 bg-slate-50 border-l-4 border-slate-400 text-slate-800 text-[14px]">
        <span className="font-bold">Escalation required.</span> {pb.escalation_note}
      </div>
    )}

    <div className="text-[19px] font-semibold text-slate-900 leading-snug mb-2">
      {pb.headline_action}
    </div>
    <div className="text-[13px] text-slate-500 mb-1">
      {payor} · ${Number(amount).toLocaleString()} denied · {aging} days aged
    </div>

    {pb.case_context && (
      <div className="mt-5 rounded-md bg-slate-50 border-l-4 border-slate-400 px-4 py-3 text-[15px] text-slate-800 leading-[1.65]">
        {pb.case_context}
      </div>
    )}

    <SectionHeading>Why you can win this</SectionHeading>
    <p className="text-[15px] text-slate-800 leading-[1.7]">{pb.appeal_argument}</p>

    {pb.code_notes && pb.code_notes.length > 0 && (
      <div className="mt-4 rounded-md bg-teal-50/60 border border-teal-200 px-4 py-3 text-[14px] text-slate-800 leading-[1.6]">
        {pb.code_notes.map((n, i) => <p key={i} className={i > 0 ? "mt-2" : ""}>{n}</p>)}
      </div>
    )}

    <SectionHeading>Documents to attach</SectionHeading>
    <ul className="space-y-2 text-[15px] text-slate-800 leading-[1.6]">
      {(pb.documents_required || []).map((d, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-teal-600 flex-shrink-0" />
          <span>{d}</span>
        </li>
      ))}
    </ul>

    <SectionHeading>What to cite</SectionHeading>
    <ul className="space-y-2 text-[15px] text-slate-800 leading-[1.6]">
      {(pb.citation_framework || []).map((c, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-teal-600 flex-shrink-0" />
          <span>{c}</span>
        </li>
      ))}
    </ul>

    <SectionHeading>How to fight {payor}</SectionHeading>
    <dl className="text-[15px] text-slate-800 leading-[1.6] space-y-3">
      <div><dt className="font-semibold text-slate-900 mb-0.5">Appeal process</dt><dd className="text-slate-700">{pb.payor_tactics?.appeal_process || "—"}</dd></div>
      <div><dt className="font-semibold text-slate-900 mb-0.5">Peer-to-peer review</dt><dd className="text-slate-700">{pb.payor_tactics?.peer_to_peer || "—"}</dd></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><dt className="font-semibold text-slate-900 mb-0.5">Filing window</dt><dd className="text-slate-700">{pb.payor_tactics?.filing_window_days ? `${pb.payor_tactics.filing_window_days} days from remittance` : "—"}</dd></div>
        <div><dt className="font-semibold text-slate-900 mb-0.5">Portal</dt><dd className="text-slate-700 break-all">{pb.payor_tactics?.portal_url || "—"}</dd></div>
      </div>
      <div><dt className="font-semibold text-slate-900 mb-0.5">Escalation path</dt><dd className="text-slate-700">{pb.payor_tactics?.escalation_path || "—"}</dd></div>
    </dl>

    {pb.common_pushback && pb.common_pushback.length > 0 && (
      <>
        <SectionHeading>Expect these payor objections</SectionHeading>
        <ul className="space-y-3 text-[15px] text-slate-800 leading-[1.6]">
          {pb.common_pushback.map((p, i) => (
            <li key={i} className="pl-4 border-l-2 border-slate-300">{p}</li>
          ))}
        </ul>
      </>
    )}

    {pb.win_notes && (
      <>
        <SectionHeading>Reality check on win probability</SectionHeading>
        <p className="text-[15px] text-slate-800 leading-[1.7]">{pb.win_notes}</p>
      </>
    )}
  </div>
);

export default function SamplePlaybookPage() {
  const [samples, setSamples] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/sample-playbooks`);
        if (cancelled) return;
        setSamples(res.data.samples || []);
        setActiveId((res.data.samples || [])[0]?.id || null);
      } catch (e) {
        if (!cancelled) setError("Could not load sample playbooks.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const active = samples.find((s) => s.id === activeId);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Top bar */}
      <div className="bg-[#0A192F] border-b-2 border-teal-500">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Sparkles className="h-5 w-5 text-teal-400" />
            <span className="font-bold text-[17px]">Revenue Cycle IQ</span>
            <span className="text-slate-400 text-[13px] ml-2 hidden sm:inline">·  Denial Intelligence & Recovery Workflow Platform</span>
          </div>
          <Link to="/login" className="text-teal-300 hover:text-teal-200 text-[14px] font-semibold" data-testid="sample-login-link">Sign in →</Link>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-4xl mx-auto px-6 pt-10 pb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-[11px] font-bold uppercase tracking-wider mb-4">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-teal-500" /> Sample · Anonymized Demo Data
        </div>
        <h1 className="text-[36px] sm:text-[44px] font-bold text-slate-900 leading-[1.1] tracking-tight">
          This is what your appeals team sees tomorrow morning.
        </h1>
        <p className="mt-5 text-[18px] text-slate-700 leading-[1.55] max-w-3xl">
          For every denied claim in your data, Revenue Cycle IQ generates a full fighting playbook —
          the appeal argument, the documents to pull, the payor-specific procedure, and the common
          payor pushback your team needs to preempt. <span className="text-slate-900 font-semibold">No more guessing. No more Googling ANSI codes.</span>
        </p>
        <p className="mt-3 text-[15px] text-slate-600">
          Purpose-built for <span className="font-semibold text-slate-900">behavioral health</span>,
          {" "}<span className="font-semibold text-slate-900">hospital</span>, and
          {" "}<span className="font-semibold text-slate-900">professional</span> revenue cycle teams.
          Pick a sample below to see the playbook your platform writes per claim.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 text-[12px] text-slate-500">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
          <span>Zero-PHI architecture · No patient identifiers ever stored or processed</span>
        </div>
      </div>

      {/* Sample selector */}
      {loading ? (
        <div className="max-w-4xl mx-auto px-6 py-12 text-center text-slate-500">Loading sample playbooks…</div>
      ) : error ? (
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="rounded-md bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-[14px] flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" /> {error}
          </div>
        </div>
      ) : (
        <>
          <div className="max-w-4xl mx-auto px-6 mb-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="sample-selector">
              {samples.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActiveId(s.id)}
                  data-testid={`sample-tab-${s.id}`}
                  className={`text-left rounded-lg border-2 px-4 py-3 transition ${
                    activeId === s.id
                      ? "border-teal-500 bg-teal-50 shadow-sm"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <div className={`text-[11px] font-bold uppercase tracking-wider mb-1 ${activeId === s.id ? "text-teal-700" : "text-slate-500"}`}>
                    {s.label}
                  </div>
                  <div className="text-[14px] font-semibold text-slate-900 leading-tight">{s.headline}</div>
                  <div className="text-[12px] text-slate-500 mt-1">{s.subhead}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Active playbook */}
          {active && (
            <div className="max-w-4xl mx-auto px-6 pb-12" data-testid="sample-playbook-active">
              <PlaybookRender
                pb={active.playbook}
                payor={active.payor}
                amount={active.amount}
                aging={active.aging}
              />
            </div>
          )}

          {/* Bottom CTA */}
          <div className="bg-[#0A192F] border-t-2 border-teal-500">
            <div className="max-w-4xl mx-auto px-6 py-12 text-center">
              <h2 className="text-[28px] sm:text-[32px] font-bold text-white leading-tight">
                See yours in 5 minutes.
              </h2>
              <p className="mt-3 text-[16px] text-slate-300 max-w-2xl mx-auto">
                Drop a CSV of your denial data. We generate a playbook like the one above for every
                denied claim in your file. No PHI required. No long implementation. No annual contract
                to start.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="mailto:intel@therevenuecycleiq.com?subject=Revenue%20Cycle%20IQ%20-%20Schedule%20a%2015-min%20call"
                  className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-bold rounded-md px-6 py-3 text-[15px] transition"
                  data-testid="sample-cta-schedule"
                >
                  Schedule a 15-min call <ArrowRight className="h-4 w-4" />
                </a>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 border border-slate-400 hover:bg-white/5 text-white font-semibold rounded-md px-6 py-3 text-[15px] transition"
                  data-testid="sample-cta-login"
                >
                  Already a customer? Sign in
                </Link>
              </div>
              <p className="mt-6 text-[12px] text-slate-500 max-w-2xl mx-auto leading-[1.6]">
                <span className="text-slate-300 font-semibold">Zero-PHI architecture.</span> Uploaded
                CSV files are scanned for PHI (names, DOB, MRN, SSN, addresses, phone, member IDs)
                and rejected at the door. Only de-identified operational analytics are stored.
                HIPAA-aligned by design — no Business Associate Agreement required.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
